/**
 * Organization API Service
 *
 * Provides standardized methods for interacting with the Organization API endpoints
 */
import api from './api';

/**
 * Organization service with standardized methods for organization-related API operations
 */
export const organizationService = {
  /**
   * Get all organizations with optional filtering
   * @param {Object} params - Query parameters (type, search, etc.)
   * @returns {Promise<Array>} List of organizations
   */
  async getOrganizations(params = {}) {
    return api.get('/organizations', params);
  },

  /**
   * Get a single organization by ID
   * @param {number|string} id - Organization ID
   * @returns {Promise<Object>} Organization data with contacts
   */
  async getOrganization(id) {
    return api.get(`/organizations/${id}`);
  },

  /**
   * Search organizations for autocomplete
   * @param {string} query - Search query
   * @param {string|null} type - Organization type filter (consultant, builder, contractor, competitor)
   * @returns {Promise<Array>} Matching organizations
   */
  async searchOrganizations(query, type = null) {
    const params = { q: query };
    if (type) {
      params.type = type;
    }
    return api.get('/organizations/search', params);
  },

  /**
   * Quick create organization with name only
   * @param {string} name - Organization name
   * @param {string|null} type - Organization type to assign
   * @returns {Promise<Object>} Created organization {id, name}
   */
  async quickCreateOrganization(name, type = null) {
    const data = { name };
    if (type) {
      data.type = type;
    }
    return api.post('/organizations/quick-create', data);
  },

  /**
   * Create a new organization with full details
   * @param {Object} organizationData - Organization data
   * @param {string} organizationData.name - Organization name
   * @param {string} [organizationData.abn] - ABN
   * @param {Array} [organizationData.types] - Organization types
   * @param {string} [organizationData.address] - Address
   * @param {string} [organizationData.city] - City
   * @param {string} [organizationData.state] - State
   * @param {string} [organizationData.postcode] - Postcode
   * @param {string} [organizationData.notes] - Notes
   * @returns {Promise<Object>} Created organization
   */
  async createOrganization(organizationData) {
    return api.post('/organizations', organizationData);
  },

  /**
   * Update an existing organization
   * @param {number|string} id - Organization ID
   * @param {Object} organizationData - Updated organization data
   * @returns {Promise<Object>} Updated organization
   */
  async updateOrganization(id, organizationData) {
    return api.put(`/organizations/${id}`, organizationData);
  },

  /**
   * Delete an organization
   * @param {number|string} id - Organization ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteOrganization(id) {
    return api.delete(`/organizations/${id}`);
  },

  // ==================== ABN Lookup ====================

  /**
   * Lookup ABN from Australian Business Register
   * @param {string} abn - The ABN to lookup (11 digits, spaces allowed)
   * @returns {Promise<Object>} ABN lookup result with entity details
   */
  async lookupABN(abn) {
    const cleanAbn = abn.replace(/\s/g, '');
    return api.get('/organizations/abn-lookup', { abn: cleanAbn });
  },

  /**
   * Search for businesses by name in Australian Business Register
   * @param {string} name - The business name to search for (min 3 characters)
   * @param {number} limit - Maximum number of results to return (default 10)
   * @returns {Promise<Object>} Search results with matching businesses
   */
  async searchABN(name, limit = 10) {
    return api.get('/organizations/abn-search', { name, limit });
  },

  // ==================== Export ====================

  /**
   * Export organizations as CSV file download
   * Uses fetch directly since the endpoint returns a CSV stream, not JSON
   * @returns {Promise<Blob>} CSV file as a Blob
   */
  async exportOrganizations() {
    const { useAuthStore } = await import('../stores/auth');
    const authStore = useAuthStore();
    const headers = {
      'Accept': 'text/csv',
    };
    if (authStore.token) {
      headers['Authorization'] = `Bearer ${authStore.token}`;
    }
    const response = await fetch('/api/organizations/export', {
      method: 'GET',
      headers,
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }
    return response.blob();
  },

  // ==================== Type-Filtered Helpers ====================

  /**
   * Get organizations by type(s)
   * @param {string|Array} types - Single type or array of types
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Array>} Filtered list of organizations
   */
  async getByType(types, params = {}) {
    const typeParam = Array.isArray(types) ? types.join(',') : types;
    return api.get('/organizations', { ...params, type: typeParam });
  },

  /**
   * Get all customers
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Array>} List of customer organizations
   */
  async getCustomers(params = {}) {
    return this.getByType('customer', params);
  },

  /**
   * Get all contractors
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Array>} List of contractor organizations
   */
  async getContractors(params = {}) {
    return this.getByType('contractor', params);
  },

  /**
   * Get all builders
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Array>} List of builder organizations
   */
  async getBuilders(params = {}) {
    return this.getByType('builder', params);
  },

  /**
   * Get all consultants
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Array>} List of consultant organizations
   */
  async getConsultants(params = {}) {
    return this.getByType('consultant', params);
  },

  /**
   * Get all competitors
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Array>} List of competitor organizations
   */
  async getCompetitors(params = {}) {
    return this.getByType('competitor', params);
  },

  // ==================== Contacts ====================

  /**
   * Get contacts for an organization
   * @param {number|string} organizationId - Organization ID
   * @returns {Promise<Array>} List of contacts
   */
  async getContacts(organizationId) {
    return api.get(`/organizations/${organizationId}/contacts`);
  },

  /**
   * Add a contact to an organization
   * @param {number|string} organizationId - Organization ID
   * @param {Object} contactData - Contact data
   * @param {string} contactData.name - Contact name
   * @param {string} [contactData.email] - Email
   * @param {string} [contactData.phone] - Phone
   * @param {string} [contactData.role] - Role/title
   * @param {boolean} [contactData.is_primary] - Is primary contact
   * @returns {Promise<Object>} Created contact
   */
  async addContact(organizationId, contactData) {
    return api.post(`/organizations/${organizationId}/contacts`, contactData);
  },

  /**
   * Update a contact
   * @param {number|string} organizationId - Organization ID
   * @param {number|string} contactId - Contact ID
   * @param {Object} contactData - Updated contact data
   * @returns {Promise<Object>} Updated contact
   */
  async updateContact(organizationId, contactId, contactData) {
    return api.put(`/organizations/${organizationId}/contacts/${contactId}`, contactData);
  },

  /**
   * Delete a contact
   * @param {number|string} organizationId - Organization ID
   * @param {number|string} contactId - Contact ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteContact(organizationId, contactId) {
    return api.delete(`/organizations/${organizationId}/contacts/${contactId}`);
  },
};

export default organizationService;
